import { hasTrackingConsent } from "./consent";

const SESSION_UUID_KEY = "flots-obs-uuid";

/** sessionStorage key written by the `/XXXXXX` context-code redirect page. */
export const CONTEXT_STORAGE_KEY = "flots-obs-context";

const CONTEXT_CODE_RE = /^[a-zA-Z0-9]{6}$/;

/** Whether `value` is a valid 6-character context code (flyer, card, partner link). */
export function isContextCode(value: string): boolean {
  return CONTEXT_CODE_RE.test(value);
}

/** Reads a sessionStorage entry, returning null when storage is blocked. */
export function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes a sessionStorage entry, ignoring blocked storage. */
export function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked (private mode, quota): the visit simply stays unidentified.
  }
}

/**
 * Returns the website session UUID, creating it on first use — only once the visitor has
 * accepted cookies. It lives in sessionStorage, so it dies with the tab.
 *
 * @returns The UUID, or null without consent, on the server, or when storage/crypto is unavailable.
 */
export function getOrCreateSessionUUID(): string | null {
  if (typeof window === "undefined") return null;
  if (!hasTrackingConsent()) return null;

  const existing = safeSessionGet(SESSION_UUID_KEY);
  if (existing) return existing;

  if (typeof crypto === "undefined" || !crypto.randomUUID) return null;
  const uuid = crypto.randomUUID();
  safeSessionSet(SESSION_UUID_KEY, uuid);
  return safeSessionGet(SESSION_UUID_KEY) === uuid ? uuid : null;
}

/**
 * Returns the existing website session UUID without creating one.
 *
 * @returns The UUID, or null when no consented session exists yet.
 */
export function getSessionUUID(): string | null {
  if (typeof window === "undefined") return null;
  return safeSessionGet(SESSION_UUID_KEY);
}

/**
 * Returns the 6-character context code of this visit (set by the `/XXXXXX` redirect page).
 *
 * @returns The code, or null when the visit did not come through a context link.
 */
export function getStoredContext(): string | null {
  if (typeof window === "undefined") return null;
  const stored = safeSessionGet(CONTEXT_STORAGE_KEY);
  return stored && isContextCode(stored) ? stored : null;
}
